<script setup lang="ts">
import { computed } from 'vue';
import { cn } from '@/shared/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'destructive' | 'brand';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    loading?: boolean;
    block?: boolean;
  }>(),
  {
    variant: 'primary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    block: false,
  },
);

defineEmits<{ (e: 'click', ev: MouseEvent): void }>();

const classes = computed(() =>
  cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium select-none transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-60',
    props.block && 'w-full',
    props.size === 'sm' && 'h-9 px-3 text-sm',
    props.size === 'md' && 'h-10 px-4 text-sm',
    props.size === 'lg' && 'h-12 px-6 text-base',
    props.size === 'icon' && 'h-10 w-10 p-0',
    props.variant === 'primary' &&
      'bg-foreground text-background hover:opacity-90 focus-visible:ring-foreground',
    props.variant === 'brand' &&
      'bg-[var(--brand-primary)] text-white shadow-sm hover:brightness-105 active:brightness-95 focus-visible:ring-[var(--brand-primary)]',
    props.variant === 'secondary' &&
      'bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-muted-foreground',
    props.variant === 'ghost' &&
      'bg-transparent text-foreground hover:bg-muted focus-visible:ring-muted-foreground',
    props.variant === 'outline' &&
      'border border-border bg-background text-foreground hover:bg-muted focus-visible:ring-muted-foreground',
    props.variant === 'destructive' &&
      'bg-destructive text-destructive-foreground hover:opacity-90 focus-visible:ring-destructive',
  ),
);
</script>

<template>
  <button :type="type" :disabled="disabled || loading" :class="classes" @click="$emit('click', $event)">
    <span v-if="loading" class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    <slot />
  </button>
</template>
